import { armorTypes } from 'app/frontend/types/custom/ArmorType'
import CharacterClass from 'app/frontend/types/custom/CharacterClass'
import UpgradeTrack, {
  upgradeTracks,
} from 'app/frontend/types/custom/UpgradeTrack'
import { AuditData } from 'app/frontend/types/custom/AuditData'

export const tierSlots = ['head', 'shoulder', 'chest', 'hands', 'legs'] as const

export type TierSlot = typeof tierSlots[number]

export type PriorityValue = 'preferred' | 'avoid' | 'obtained' | 'in_vault'

export const armorTypeOrder = ['Cloth', 'Leather', 'Mail', 'Plate'] as const

export type ArmorType = typeof armorTypeOrder[number]

export type SeasonTierItems = Partial<
  Record<TierSlot, { ilvl?: number | string; difficulty?: string }>
>

export type MemberRow = {
  member: wowaudit.TeamMembership
  armorType?: ArmorType
  obtained: number
  feat?: string
  priorities: Record<TierSlot, PriorityValue | 'unset'>
  storedPriority: Record<string, string>
  tierItems: SeasonTierItems | undefined
}

export const priorityOptions = [
  {
    label: 'Not set',
    value: 'unset',
    className: 'text-muted-foreground',
    background: 'bg-card hover:bg-card',
    sortLabel: '',
  },
  {
    label: 'Preferred',
    value: 'preferred',
    className: 'text-info-foreground',
    background: 'bg-info hover:bg-info/80',
    sortLabel: '',
  },
  {
    label: 'In vault',
    value: 'in_vault',
    className: 'text-info-foreground',
    background: 'bg-info hover:bg-info/80',
    sortLabel: '',
  },
  {
    label: 'Avoid',
    value: 'avoid',
    className: 'text-warning-foreground',
    background: 'bg-warning hover:bg-warning/80',
    sortLabel: '',
  },
  {
    label: 'Obtained',
    value: 'obtained',
    className: 'text-success-foreground',
    background: 'bg-success hover:bg-success/80',
    sortLabel: '',
  },
]

export const armorTypeForClass = (
  characterClass: CharacterClass,
): ArmorType | undefined => {
  return Object.entries(armorTypes).find(([, classes]) =>
    classes.includes(characterClass),
  )?.[0] as ArmorType | undefined
}

export const seasonTierItems = (
  auditRow: AuditData[number] | undefined,
  seasonId: number,
): SeasonTierItems | undefined => auditRow?.tier_items?.[String(seasonId)]

export const seasonCatalystCharge = (
  auditRow: AuditData[number] | undefined,
  seasonId: number,
  currentSeasonId: number,
): string | undefined => {
  const stored = auditRow?.catalyst_charges?.[String(seasonId)]
  if (stored) return stored
  if (seasonId !== currentSeasonId) return undefined
  return auditRow?.last_refresh?.catalyst_charge_completed
}

export const detectedPiece = (
  slot: TierSlot,
  tierItems: SeasonTierItems | undefined,
) => {
  const letter = tierItems?.[slot]?.difficulty
  const track = letter
    ? upgradeTracks.find((candidate) => candidate[0] === letter)
    : undefined
  const ilvl = tierItems?.[slot]?.ilvl
  return { track, ilvl }
}

export const isAutoObtained = (
  slot: TierSlot,
  tierItems: SeasonTierItems | undefined,
  minimumTrack: UpgradeTrack,
) => {
  const { track } = detectedPiece(slot, tierItems)
  if (!track) return false
  return upgradeTracks.indexOf(track) <= upgradeTracks.indexOf(minimumTrack)
}

export const effectivePriority = (
  slot: TierSlot,
  storedPriority: Record<string, string>,
  tierItems: SeasonTierItems | undefined,
  minimumTrack: UpgradeTrack,
): PriorityValue | 'unset' => {
  if (isAutoObtained(slot, tierItems, minimumTrack)) return 'obtained'
  return (storedPriority[slot] as PriorityValue | undefined) ?? 'unset'
}

export const obtainedCount = (
  storedPriority: Record<string, string>,
  tierItems: SeasonTierItems | undefined,
  minimumTrack: UpgradeTrack,
) => {
  return tierSlots.filter(
    (slot) =>
      isAutoObtained(slot, tierItems, minimumTrack) ||
      storedPriority[slot] === 'obtained',
  ).length
}

export const obtainedCountColorClass = (count: number) => {
  if (count >= 4) return 'text-success-foreground'
  if (count >= 2) return 'text-warning-foreground'
  return 'text-destructive-foreground'
}
